import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { fetchDashboardOverviewByClient, fetchDashboardOverviewByChannel, fetchDashboardOverviewByCampaign } from "@src/api/actions/dashboard/overview";
import { useAuth } from "@src/context";
import { formatDateToYMD } from "@src/utils/dateUtils";

const OverviewContext = React.createContext(null);

export const useOverview = () => useContext(OverviewContext);

export const OverviewProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();

  // Filters (expand as needed)
  const [clientId, setClientId] = useState(6);   // Default clientId (or could be fetched from somewhere)
  const [startDate, setStartDate] = useState(new Date()); // default: today
  const [endDate, setEndDate] = useState(new Date()); // default: today

  // Data state
  const [metrics, setMetrics] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  // Filters are recalculated when clientId, startDate, or endDate changes
  const filters = useMemo(() => ({
    ...(clientId ? { client_id: clientId } : {}),
    date_after: formatDateToYMD(startDate),
    date_before: formatDateToYMD(endDate),
  }), [clientId, startDate, endDate]);

  // Make the API call to fetch the data whenever filters change
  useEffect(() => {
    if (isAuthenticated && clientId !== null) {
      setIsLoading(true);
      console.log('Filters:', filters); // Log filters to check their values

      let fetchData;
      if (clientId) {
        fetchData = fetchDashboardOverviewByClient(filters);
      } else if (startDate && endDate) {
        fetchData = fetchDashboardOverviewByChannel(filters);
      } else {
        fetchData = fetchDashboardOverviewByCampaign(filters);
      }

      fetchData
        .then(res => {
          console.log('API Response:', res); // Log response data for debugging
          const data = res.data?.[0] || {}; // Make sure we access the first element in the data array
          console.log('Processed Data:', data);  // Check the data you're passing to setMetrics

          // Set metrics based on the correct data structure
          setMetrics([
            { label: 'Total Leads', value: data.total_leads_sum || 0 },
            { label: 'Appointments Set', value: data.booked_appointments_sum || 0 },
            { label: 'Close Rate', value: `${data.close_rate_avg || 0}%` },
            { label: 'Cost Per Lead', value: `$${data.cost_per_lead_avg || 0}` },
          ]);

          setIsLoading(false);
        })
        .catch((error) => {
          console.error('API Error:', error);  // Log the error for debugging
          setIsLoading(false);
          setIsError(true);
        });
    }
  }, [isAuthenticated, filters, clientId, startDate, endDate]);  // Ensure filters are in the dependency array

  // Log the updated metrics state whenever it changes
  useEffect(() => {
    console.log('Updated metrics:', metrics);  // Log the updated metrics whenever they change
  }, [metrics]);

  return (
    <OverviewContext.Provider value={{
      metrics, chartData, isLoading, isError,
      clientId, setClientId, startDate, setStartDate, endDate, setEndDate,
    }}>
      {children}
    </OverviewContext.Provider>
  );
};
